<template>
  <v-container fluid justify-start>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredEvents"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      class="elevation-2"
      >

      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>
        <!-- Header Toolbar -->
        <v-toolbar dense dark flat :color="appColor_Medium">
          
          <app-back-button v-bind:color="appColor_Dark" />

          <v-toolbar-title>Playlist Events</v-toolbar-title>

        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">
          <!-- Verbose -->
          <v-col cols="12" md="1">
            <v-checkbox
              v-model="verbose"
              :color="appColor_Selected"
              label="Verbose"
            />
          </v-col>

          <v-col cols="12" sm="1">
            <v-select
              v-model="context"
              :items="contexts"
              label="Context"
              :disabled="isLoading"
              class="ml-4"
            >
          </v-select>
          </v-col>

          <v-col cols="12" sm="1">
            <v-select
              v-model="playlistType"
              :items="playlistTypes"
              label="Type"
              :disabled="isLoading"
              class="ml-4"
            >
          </v-select>
          </v-col>

          <!-- Search -->
          <v-col cols="12" md="9">
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
              :disabled="isLoading"
              clearable
              class="ml-4"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.username="{ item }">
        <span class="text-left">
          {{ `${item.username} (${item.email})`  }}
        </span>
      </template>

      <template v-slot:item.description="{ item }">
        <span v-if="item.description.startsWith('Item')" @click="displayMediaDetail(item)" :style="appStyle_HyperLink">
          {{ getItemDescription(item) }}
        </span>

        <span v-else-if="item.errorMessage === null" class="text-left">
          {{ item.description }}
        </span>

        <span v-else @click="displayError(item)" :style="appStyle_HyperLink">
          {{ getErrorLabel(item) }}
        </span>
      </template>
      
      <template v-slot:item.createDate="{ item }">
        <span class="text-left"><app-date :date="item.createDate" /></span>
      </template>

    </v-data-table>

  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { AppColor, AppStyle } from '@/config.js'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'

export default {
  name: 'UserEvents',
  components: {
    AppDate,
    AppBackButton
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      
      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      isLoading: true,
      search: '',
      verbose: false,
      context: 'ABBY',
      playlistType: 'Kiosk',

      contexts: ['ABBY', 'GENIE'],
      playlistTypes: ['Kiosk', 'Resident'],

      playlistEvents: {
        abbyKiosk: [],
        abbyResident: null,
        genieKiosk: null,
        genieResident: null
      }
    }
  },
  computed: {
    noDataText () {
      if (this.isLoading) return 'One moment...'

      return this.events.length === 0
        ? 'No playlist events found'
        : ''
    },
    isAbby () {
      return this.context === 'ABBY'
    },
    isKiosk () {
      return this.playlistType === 'Kiosk'
    },
    headers () {
      var headers = []
      headers.push(
      {
        text: 'Performed By',
        value: 'username',
        align: 'left',
        width: !this.isKiosk ? '25%' : '30%',
        class: 'pr-0'
      })
      headers.push({
        text: 'Role',
        value: 'performedByRole',
        align: 'left',
        width: '5%',
        class: 'pr-0'
      })  
      headers.push({
        text: 'Description',
        value: 'description',
        align: 'left',
        width: !this.isKiosk ? '13%' : '23%',
        class: 'pr-0'
      })
      headers.push({
        text: 'Kiosk',
        value: 'kioskName',
        align: 'left',
        width: '10%',
        class: 'pr-0'
      })
      if (!this.isKiosk)
        headers.push({
          text: 'Resident',
          value: 'residentName',
          align: 'left',
          width: '10%',
          class: 'pr-0'
        })
      headers.push({
        text: 'Playlist',
        value: 'targetName',
        align: 'left',
        width: '15%',
        class: 'pr-0'
      })
      headers.push({
        text: 'Date',
        value: 'createDate',
        align: 'left',
        width: '12%',
        class: 'pr-0'
      })
      return headers
    },
    events () {
      var events = []
      if (this.isAbby && this.isKiosk) {
        events = this.playlistEvents.abbyKiosk
      }
      else if (this.isAbby && !this.isKiosk) {
        if (this.playlistEvents.abbyResident === null)
          this.loadloadAbbyResidentPlaylistEvents()
          events = this.playlistEvents.abbyResident
      }
      else if (!this.isAbby && this.isKiosk) {
        if (this.playlistEvents.genieKiosk === null)
          this.loadloadGenieKioskPlaylistEvents()
          events = this.playlistEvents.genieKiosk
      }
      else {
        if (this.playlistEvents.genieResident === null)
          this.loadloadGenieResidentPlaylistEvents()
          events = this.playlistEvents.genieResident
      }

      return events
    },
    filteredEvents () {
      if (this.isLoading) return []
      if (this.events == null) return []
      const self = this

      return self.events.filter(e => {
        return (
          self.verbose || (!e.description.startsWith('Attempting to') && !e.description.indexOf('Error') >= 0)
        )
      })
    }
  },
  methods: {
    loadData() {
      this.loadloadAbbyKioskPlaylistEvents()
    },

    loadloadAbbyKioskPlaylistEvents () {
      const self = this
      self.isLoading = true
      appService.getAbbyKioskPlaylistEvents().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.playlistEvents.abbyKiosk = data.events
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    loadloadAbbyResidentPlaylistEvents () {
      const self = this
      self.isLoading = true
      appService.getAbbyResidentPlaylistEvents().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.playlistEvents.abbyResident = data.events
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    loadloadGenieKioskPlaylistEvents () {
      const self = this
      self.isLoading = true
      appService.getGenieKioskPlaylistEvents().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.playlistEvents.genieKiosk = data.events
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    loadloadGenieResidentPlaylistEvents () {
      const self = this
      self.isLoading = true
      appService.getGenieResidentPlaylistEvents().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.playlistEvents.genieResident = data.events
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    customFilter (value, search, item) {
      const description = item.errorMessage !== null
        ? this.getErrorLabel(item)
        : item.description.startsWith('Item')
          ? this.getItemDescription(item)
          : item.description

      return (
        (value != null &&
          search != null &&
          item.username
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          item.performedByRole
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          description
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          (item.targetName ?? '')
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          item.kioskName
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          (item.residentName ?? '')
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1
      ))
    },

    displayMediaDetail(item) {
      this.$alert.show({
        icon: 'info',
        formColor: 'info',
        title: 'Media Detail',
        width: 400,
        text: `<b>Title</b>: ${item.title}</br></br><b>Blob Name</b>: ${item.blobName}`
      })
    },

    getItemDescription (item) {
      var mediaType = ''
      const pathImage = this.isAbby ? 'images' : 'image'
      const pathVideo = this.isAbby ? 'videos' : 'video'

      if (item.blobName.indexOf(`${pathImage}/`) >= 0)
        mediaType = 'Image'
      else if (item.blobName.indexOf(`${pathVideo}/`) >= 0)
        mediaType = 'Video'
      else if (item.blobName.indexOf('audio/') >= 0)
        mediaType = 'Audio'
      
      if (item.blobName.indexOf('announcement/') >= 0)
        mediaType = 'Annoucement'

      if (item.description.indexOf('added') >= 0) {
        return `${mediaType} Added`
      } else if (item.description.indexOf('removed') >= 0) {
        return `${mediaType} Removed`
      }
    },

    getErrorLabel (item) {
      if (item.description.indexOf('invalid data') >= 0) {
        return 'Validation Failed'
      } else if (item.description.indexOf('Error occurred') >= 0) {
        return 'Internal Error'
      }
    },

    displayError (item) {
      var errorSeverity = null

      if (item.description.indexOf('invalid data') >= 0) 
        errorSeverity = 0
      else if (item.description.indexOf('Error occurred') >= 0)
        errorSeverity = 1

      this.handleError({
        errorSeverity: errorSeverity,
        errorMessage: item.errorMessage
      })
    },

    handleError (result) {
      this.$error.show({
        title:
          result.errorSeverity === 0 ? 'Validation Failed' : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  }
}
</script>
